import type { FC, ComponentType, SVGProps } from 'react';
import { motion } from 'motion/react';
import ServingFood from 'assets/svg/icons/ServingFood';
import ArrowsEuro from 'assets/svg/icons/ArrowsEuro';
import { LaptopIcon, ChairIcon } from 'components/atoms/Icons/SpaceIcons';

// Badge type definition
export type ExpenseBadgeType =
  | 'lunch'
  | 'laptop'
  | 'subscriptions'
  | 'furniture';

interface ExpenseBadgeProps {
  type: ExpenseBadgeType;
  title: string;
  amount: string;
  delay?: number;
}

// Icon mapping for each expense type
const ICON_MAP: Record<
  ExpenseBadgeType,
  ComponentType<SVGProps<SVGSVGElement> | { className?: string }>
> = {
  lunch: ServingFood,
  laptop: LaptopIcon,
  subscriptions: ArrowsEuro,
  furniture: ChairIcon,
};

/**
 * Expense badge component that displays an icon, title and amount
 * Used in the IssueCardStep animation
 */
const ExpenseBadge: FC<ExpenseBadgeProps> = ({
  type,
  title,
  amount,
  delay = 0,
}) => {
  const Icon = ICON_MAP[type];

  return (
    <motion.div
      className="flex items-center gap-2 bg-white rounded-full py-2 px-3 shadow-lg"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.2,
        delay,
        ease: [0, 0, 0.2, 1], // ease-out
      }}
    >
      {/* Icon container */}
      <div className="flex items-center justify-center">
        <Icon className="w-5 h-5 text-bank-800" />
      </div>

      {/* Text content */}
      <div className="flex flex-col">
        <span className="text-xs text-gray-600 leading-tight">{title}</span>
        <span className="text-xs font-medium text-black leading-tight">
          {amount}
        </span>
      </div>
    </motion.div>
  );
};

export default ExpenseBadge;
